import { useCallback, useEffect, useRef } from 'react';
import { useDebounce, useScroll } from 'react-use';

function findScrollPositions(
  listSize: number,
  childWidth: number,
  gap: number,
) {
  const edgeElementsWidth = childWidth - gap;
  const otherElementsWidth = childWidth + gap;

  const scrollPositions = [0, edgeElementsWidth];

  for (let i = 2; i < listSize; i++) {
    if (i === listSize - 1) {
      scrollPositions[i] = scrollPositions[i - 1] + edgeElementsWidth;
    } else {
      scrollPositions[i] = scrollPositions[i - 1] + otherElementsWidth;
    }
  }

  return scrollPositions;
}

function useOnSlideHandler(listRef, width, onSlide, gapOffset = 0) {
  const latestOnSlide = useRef(onSlide);
  const lookupRef = useRef<number[]>([]);
  const scrollState = useScroll(listRef);

  const buildLookup = useCallback(
    (listElement: HTMLElement | null) => {
      if (!listElement) {
        return [];
      }
      const firstChild = listElement.children[0] as HTMLElement | undefined;
      return findScrollPositions(
        listElement.children.length,
        firstChild?.offsetWidth ?? 0,
        gapOffset,
      );
    },
    [gapOffset],
  );

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }
    lookupRef.current = buildLookup(listElement);
  }, [listRef, width, onSlide, buildLookup]);

  useDebounce(
    () => {
      const listElement = listRef.current;
      if (!listElement || !onSlide) {
        return;
      }
      if (lookupRef.current.length === 0) {
        lookupRef.current = buildLookup(listElement);
      }

      latestOnSlide.current = onSlide;
      const scrollLeft = listElement.scrollLeft;
      const childWidth = listElement.children[0]?.offsetWidth ?? 0;

      if (scrollLeft === 0 || childWidth === 0) {
        latestOnSlide.current(0);
      } else {
        const closestPosition = lookupRef.current.findIndex(
          (position) => position >= scrollLeft,
        );
        latestOnSlide.current(closestPosition);
      }
    },
    40,
    [scrollState.x, onSlide, gapOffset, width],
  );
}

export default useOnSlideHandler;
